import styled from '@emotion/styled';
import { Select, Spin, Tabs, message } from 'antd';
import { simpleStringify, useQuery } from 'components/RouterQuery';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { uniqBy } from 'lodash-es';
import dayjs from 'lib/dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useNavigate } from 'react-router-dom';
import { assistantAuth } from 'services/api/dashboard';
import {
  language2currencyMap,
  regionFixedExchangeRate,
  RegionFixedExchangeRateValue,
} from 'services/config/region';
import DataUpdate from '../DataUpdate';
import { DashboardCurrencyCtx, useDashboardCurrency } from '../ImplementData/hooks/useCurrency';
import AssistantPerf from './components/AssistantPerf';
import Robot from './components/Robot';
import NotOpen from './NotOpen';
import ReceptionScore from './RececptionScore';
import { useAuthorization } from 'components/Authorization';
import chatttSDK from 'services/chattt';
import posthog from '@leyan/lytics';

export const StyledTabs = styled(Tabs)`
  margin-top: 16px;
  .ant-tabs-nav {
    background-color: #ffffff;
    margin-bottom: 0;
    padding: 0 16px;
    border-radius: 4px 4px 0 0;
  }
`;
const Extra = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  align-items: center;
  .desc {
    color: #84868c;
  }
  .ant-select-selector,
  .ant-select-selection-placeholder {
    color: #336fff;
  }
`;

const UISpin = styled(Spin)`
  display: block;
  height: 100%;
  .ant-spin-dot {
    top: 30%;
    zoom: 2.5;
  }
`;

interface Props {
  onCurrencyChange: (val: RegionFixedExchangeRateValue) => void;
}

const currencyOptions = uniqBy(
  Object.values(regionFixedExchangeRate).map(({ region, shorthand, ...rest }) => ({
    label: shorthand,
    value: region,
    region,
    shorthand,
    ...rest,
  })),
  'shorthand',
);
const ExtraInfo = (props: Props) => {
  const { t } = useTranslate();
  const { onCurrencyChange } = props;
  const { currency } = useDashboardCurrency();
  return (
    <Extra>
      {currency && currency.region !== 'CN' && (
        <div className="desc">
          {t('ViewDashboard.履约数据.汇率提示', {
            info: `1RMB≈${currency.rate}${currency.shorthand}`,
          })}
        </div>
      )}
      <div>
        <span>{t('ViewDashboard.履约数据.金额货币为')}</span>
        <Select
          options={currencyOptions}
          allowClear
          bordered={false}
          value={currency?.region}
          placeholder="CNY"
          dropdownMatchSelectWidth={false}
          onChange={(_, option) => {
            onCurrencyChange(option as any);
          }}
        />
      </div>
    </Extra>
  );
};

interface QueryValue {
  activeTab?: 'assistantPref' | 'robot' | 'receptionScore';
}

const PerfReport = () => {
  const { t } = useTranslate();
  const { t: trans } = useTranslate();
  const [currency, setCurrency] = useState<RegionFixedExchangeRateValue>();
  const [dataUpdateTime, setDataUpdateTime] = useState<string>();
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const nav = useNavigate();

  const query = useQuery<QueryValue>();

  const { activeTab = 'assistantPref' } = query;

  const [isOpen, setIsOpen] = useState<boolean>();

  const getPermission = useGetPermission();

  const { whiteListConfig: { ASSISTANT_AND_ROBOT = false, SCORE_SCENARIO = false } = {} } =
    useOrgConfigStateValue();

  useEffect(() => {
    assistantAuth().then((open) => {
      setIsOpen(open);
    });
  }, []);

  const extraNode = useMemo(() => {
    if (activeTab === 'receptionScore') {
      return null;
    }

    return {
      right: (
        <DataUpdate
          content={
            dataUpdateTime
              ? t('ViewDashboard.nav.每日更新昨日数据', {
                  time: dayjs(dataUpdateTime).format('YYYY-MM-DD HH:mm:ss'),
                })
              : undefined
          }
        >
          <ExtraInfo onCurrencyChange={setCurrency} />
        </DataUpdate>
      ),
    };
  }, [activeTab, dataUpdateTime, t]);
  const ctx = useMemo(() => {
    return {
      currency,
      onChange: setCurrency,
      dataUpdateTime,
      setDataUpdateTime,
    };
  }, [currency, setCurrency, dataUpdateTime, setDataUpdateTime]);
  const lang = useSystemLanguageValue();
  useEffect(() => {
    const next = language2currencyMap[lang];
    setCurrency(next);
  }, [lang]);

  if (isOpen === undefined) {
    return <UISpin />;
  }

  return isOpen ? (
    <DashboardCurrencyCtx.Provider value={ctx}>
      <StyledTabs
        animated
        activeKey={activeTab}
        onChange={(activeTab) => {
          if (activeTab === 'assistantPrefAndRobot') {
            if (payload) {
              const { orgId } = payload;
              const url = `https://ipush.leyantech.com/ovs/api/ovs/v2/login?org_id=${orgId}&platform=dashboard_jump&product=chatpp&dashboard=122&exp=86400`;
              posthog.capture('$openMetabaseURL', {
                url,
              });
              chatttSDK?.app.createWindow(url, {
                width: 1024,
                height: 640,
                webPreferences: {
                  // 临时 session
                  partition: Date.now().toString(),
                },
              });
            } else {
              message.error(trans('操作失败，请稍后再试'));
            }
            return;
          }
          nav(`?${simpleStringify({ ...query, activeTab })}`);
        }}
        tabBarExtraContent={extraNode}
        destroyInactiveTabPane
      >
        <Tabs.TabPane key="assistantPref" tab={t('ViewDashboard.绩效报告.tab.客服绩效')}>
          <AssistantPerf />
        </Tabs.TabPane>
        {SCORE_SCENARIO && (
          <Tabs.TabPane key="receptionScore" tab={trans('aide.接待评分')}>
            <ReceptionScore />
          </Tabs.TabPane>
        )}
        {getPermission('绩效报告-查看部门绩效') && (
          <Tabs.TabPane key="robot" tab={t('ViewDashboard.绩效报告.tab.机器人贡献')}>
            <Robot />
          </Tabs.TabPane>
        )}
        {ASSISTANT_AND_ROBOT && chatttSDK && (
          <Tabs.TabPane
            key="assistantPrefAndRobot"
            tab={`${t('ViewDashboard.绩效报告.tab.客服绩效')}(${t('ViewDashboard.人机结合')})`}
          />
        )}
      </StyledTabs>
    </DashboardCurrencyCtx.Provider>
  ) : (
    <NotOpen />
  );
};

export default PerfReport;
